import React from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { keyframes } from "@emotion/react";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import ViewModuleOutlinedIcon from "@mui/icons-material/ViewModuleOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import QrCodeScannerOutlinedIcon from "@mui/icons-material/QrCodeScannerOutlined";
import StyleOutlinedIcon from "@mui/icons-material/StyleOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";

const motionSafe = {
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

const rise = keyframes`
  from { opacity: 0; transform: translateY(10px) scale(0.97); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const pop = keyframes`
  0%, 12% { opacity: 0.25; transform: translateY(6px) scale(0.9); }
  35%, 68% { opacity: 1; transform: translateY(0) scale(1); }
  100% { opacity: 0.35; transform: translateY(3px) scale(0.95); }
`;

const stackDrop = keyframes`
  0%, 15% { opacity: 0.3; transform: translate(18px, -10px) scale(0.88); }
  45%, 70% { opacity: 1; transform: translate(0, 0) scale(1); }
  100% { opacity: 0.4; transform: translate(4px, -2px) scale(0.94); }
`;

const slideIntoAlbum = keyframes`
  0%, 10% { opacity: 0.2; transform: translateX(-14px) scale(0.9); }
  40%, 72% { opacity: 1; transform: translateX(0) scale(1); }
  100% { opacity: 0.3; transform: translateX(6px) scale(0.95); }
`;

const pulseArrow = keyframes`
  0%, 100% { transform: translateX(0); opacity: 0.4; }
  50% { transform: translateX(4px); opacity: 1; }
`;

const bob = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
`;

const scanSweep = keyframes`
  0% { transform: translateY(2px); opacity: 0.2; }
  45% { opacity: 1; }
  100% { transform: translateY(28px); opacity: 0.15; }
`;

function MiniStage({ children, tint }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        width: 88,
        height: 64,
        flexShrink: 0,
        borderRadius: 1.5,
        overflow: "hidden",
        bgcolor: tint,
        border: "1px solid",
        borderColor: alpha("#000", 0.06),
      }}
    >
      {children}
    </Box>
  );
}

function ViewsPreview({ color }) {
  return (
    <MiniStage tint={alpha(color, 0.12)}>
      <Box
        sx={{
          position: "absolute",
          left: 8,
          top: 10,
          width: 30,
          height: 22,
          borderRadius: 0.75,
          bgcolor: "#fff",
          border: "1.5px solid",
          borderColor: alpha(color, 0.45),
          animation: `${pop} 3.6s ease-in-out infinite`,
          ...motionSafe,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          right: 8,
          top: 10,
          width: 30,
          height: 44,
          borderRadius: 0.75,
          bgcolor: "#fff",
          border: "1.5px solid",
          borderColor: alpha(color, 0.35),
          animation: `${pop} 3.6s ease-in-out infinite`,
          animationDelay: "0.2s",
          ...motionSafe,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          left: 8,
          bottom: 8,
          width: 30,
          height: 14,
          borderRadius: 0.5,
          bgcolor: alpha(color, 0.2),
          animation: `${bob} 2s ease-in-out infinite`,
          ...motionSafe,
        }}
      />
    </MiniStage>
  );
}

function StackPreview({ color }) {
  return (
    <MiniStage tint={alpha(color, 0.12)}>
      <Box
        sx={{
          position: "absolute",
          left: 18,
          top: 14,
          width: 28,
          height: 36,
          borderRadius: 1,
          bgcolor: "#fff",
          border: "1.5px solid",
          borderColor: alpha(color, 0.4),
        }}
      />
      <Box
        sx={{
          position: "absolute",
          left: 36,
          top: 18,
          width: 28,
          height: 36,
          borderRadius: 1,
          bgcolor: "#fff",
          border: "1.5px solid",
          borderColor: color,
          boxShadow: `0 4px 10px ${alpha(color, 0.2)}`,
          animation: `${stackDrop} 2.8s ease-in-out infinite`,
          ...motionSafe,
        }}
      />
    </MiniStage>
  );
}

function AlbumPreview({ color }) {
  return (
    <MiniStage tint={alpha(color, 0.12)}>
      <Box
        sx={{
          position: "absolute",
          right: 8,
          top: 10,
          width: 36,
          height: 44,
          borderRadius: 1,
          bgcolor: alpha(color, 0.18),
          border: "1.5px solid",
          borderColor: alpha(color, 0.5),
        }}
      />
      <Box
        sx={{
          position: "absolute",
          left: 8,
          top: 18,
          width: 22,
          height: 28,
          borderRadius: 0.75,
          bgcolor: "#fff",
          border: "1.5px solid",
          borderColor: alpha(color, 0.4),
          animation: `${slideIntoAlbum} 2.6s ease-in-out infinite`,
          ...motionSafe,
        }}
      />
    </MiniStage>
  );
}

function NotesPreview({ color }) {
  return (
    <MiniStage tint={alpha(color, 0.12)}>
      <Box
        sx={{
          position: "absolute",
          left: 10,
          top: 8,
          width: 32,
          height: 40,
          borderRadius: 1,
          bgcolor: "#fff",
          border: "1.5px solid",
          borderColor: alpha(color, 0.4),
          animation: `${pop} 3.4s ease-in-out infinite`,
          ...motionSafe,
        }}
      />
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            right: 10,
            top: 14 + i * 12,
            width: 28 - i * 4,
            height: 6,
            borderRadius: 0.5,
            bgcolor: alpha(color, 0.25 + i * 0.12),
            animation: `${bob} 2.2s ease-in-out infinite`,
            animationDelay: `${i * 0.1}s`,
            ...motionSafe,
          }}
        />
      ))}
    </MiniStage>
  );
}

function MoodboardPreview({ color }) {
  return (
    <MiniStage tint={alpha(color, 0.12)}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            left: 10 + i * 18,
            top: 12 + (i % 2) * 6,
            width: 20,
            height: 26,
            borderRadius: 0.75,
            bgcolor: "#fff",
            border: "1.5px solid",
            borderColor: alpha(color, 0.4),
            animation: `${pop} 3.8s ease-in-out infinite`,
            animationDelay: `${i * 0.12}s`,
            ...motionSafe,
          }}
        />
      ))}
      <Box
        sx={{
          position: "absolute",
          right: 6,
          bottom: 6,
          px: 0.5,
          py: 0.2,
          borderRadius: 0.75,
          bgcolor: alpha(color, 0.2),
          border: "1px solid",
          borderColor: alpha(color, 0.45),
          animation: `${bob} 2s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <Typography sx={{ fontSize: "0.45rem", fontWeight: 900, color }}>
          MB
        </Typography>
      </Box>
    </MiniStage>
  );
}

/** QR → movimiento o solo feed */
function ScanPreview({ color }) {
  return (
    <MiniStage tint={alpha(color, 0.12)}>
      <Box
        sx={{
          position: "absolute",
          left: 8,
          top: 10,
          width: 28,
          height: 36,
          borderRadius: 1,
          bgcolor: "#fff",
          border: "1.5px solid",
          borderColor: alpha(color, 0.45),
          display: "grid",
          placeItems: "center",
          overflow: "hidden",
        }}
      >
        <QrCodeScannerOutlinedIcon sx={{ fontSize: 16, color }} />
        <Box
          sx={{
            position: "absolute",
            left: 2,
            right: 2,
            height: 2,
            bgcolor: color,
            animation: `${scanSweep} 1.8s ease-in-out infinite`,
            ...motionSafe,
          }}
        />
      </Box>
      <Box
        sx={{
          position: "absolute",
          left: "42%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          color,
          animation: `${pulseArrow} 1.5s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />
      </Box>
      <Box
        sx={{
          position: "absolute",
          right: 6,
          top: 8,
          width: 26,
          height: 22,
          borderRadius: 0.75,
          bgcolor: alpha("#6a1b9a", 0.18),
          border: "1px solid",
          borderColor: alpha("#6a1b9a", 0.45),
          display: "grid",
          placeItems: "center",
          animation: `${bob} 2.2s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <StyleOutlinedIcon sx={{ fontSize: 12, color: "#6a1b9a" }} />
      </Box>
      <Box
        sx={{
          position: "absolute",
          right: 6,
          bottom: 8,
          width: 26,
          height: 18,
          borderRadius: 0.75,
          bgcolor: alpha(color, 0.18),
          border: "1px solid",
          borderColor: alpha(color, 0.45),
          animation: `${pop} 3.2s ease-in-out infinite`,
          animationDelay: "0.25s",
          ...motionSafe,
        }}
      />
    </MiniStage>
  );
}

const STEPS = [
  { key: "views", Preview: ViewsPreview, accent: "primary" },
  { key: "stack", Preview: StackPreview, accent: "secondary" },
  { key: "album", Preview: AlbumPreview, accent: "info" },
  { key: "scan", Preview: ScanPreview, accent: "secondary" },
  { key: "notes", Preview: NotesPreview, accent: "success" },
  { key: "moodboard", Preview: MoodboardPreview, accent: "warning" },
];

function StepCard({ Preview, title, body, accent, theme, delayMs }) {
  const color = theme.palette[accent]?.main || theme.palette.primary.main;
  return (
    <Box
      sx={{
        display: "flex",
        gap: 1.35,
        p: 1.35,
        borderRadius: 2.5,
        border: "1.5px solid",
        borderColor: alpha(color, 0.28),
        bgcolor: alpha(color, 0.06),
        boxShadow: `0 4px 14px ${alpha(color, 0.08)}`,
        animation: `${rise} 320ms ease both`,
        animationDelay: `${delayMs}ms`,
        ...motionSafe,
      }}
    >
      <Preview color={color} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 800, lineHeight: 1.25, mb: 0.4, color }}
        >
          {title}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ lineHeight: 1.45 }}
        >
          {body}
        </Typography>
      </Box>
    </Box>
  );
}

/** Dialog «Cómo funciona» para Galería / Detalle de media en reuniones. */
export default function MeetingMediaHelpDialog({ open, onClose }) {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      keepMounted={false}
      transitionDuration={160}
      className="meeting-no-print"
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(10px)",
            backgroundColor: "rgba(15, 23, 42, 0.45)",
          },
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
          maxHeight: "min(92vh, 860px)",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 1.5,
          flexShrink: 0,
          borderBottom: "1px solid",
          borderColor: "divider",
          background: (th) =>
            `linear-gradient(135deg, ${alpha(th.palette.primary.main, 0.12)} 0%, ${alpha(
              th.palette.secondary.main,
              0.08,
            )} 100%)`,
        }}
      >
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={1}
        >
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{ minWidth: 0 }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1.5,
                display: "grid",
                placeItems: "center",
                bgcolor: alpha(theme.palette.primary.main, 0.16),
                color: "primary.main",
                flexShrink: 0,
              }}
            >
              <HelpOutlineOutlinedIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 800, lineHeight: 1.15 }}
              >
                {t("Meetings.mediaHelpTitle")}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", lineHeight: 1.4, mt: 0.3 }}
              >
                {t("Meetings.mediaHelpIntro")}
              </Typography>
            </Box>
          </Stack>
          <IconButton
            aria-label={t("Meetings.cancel")}
            onClick={onClose}
            size="small"
          >
            <CloseOutlinedIcon />
          </IconButton>
        </Stack>
      </Box>

      <Box
        sx={{
          px: 2.5,
          pt: 1.75,
          pb: 1,
          overflow: "auto",
          flex: 1,
          minHeight: 0,
        }}
      >
        <Stack spacing={1.2}>
          {STEPS.map(({ key, Preview, accent }, i) => (
            <StepCard
              key={key}
              Preview={Preview}
              title={t(`Meetings.mediaHelp.${key}Title`)}
              body={t(`Meetings.mediaHelp.${key}Body`)}
              accent={accent}
              theme={theme}
              delayMs={40 + i * 45}
            />
          ))}
        </Stack>
        <Box
          sx={{
            mt: 2,
            p: 1.35,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.text.primary, 0.04),
            border: "1px dashed",
            borderColor: "divider",
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", lineHeight: 1.45, fontWeight: 600 }}
          >
            {t("Meetings.mediaHelpFootnote")}
          </Typography>
        </Box>
      </Box>

      <Stack
        direction="row"
        justifyContent="flex-end"
        sx={{
          px: 2.5,
          py: 1.75,
          borderTop: "1px solid",
          borderColor: "divider",
          flexShrink: 0,
          bgcolor: (th) => alpha(th.palette.background.default, 0.65),
        }}
      >
        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            minHeight: 42,
            borderRadius: 2,
            fontWeight: 800,
            px: 2.5,
          }}
        >
          {t("Meetings.mediaHelpClose")}
        </Button>
      </Stack>
    </Dialog>
  );
}
